import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, X, BellRing } from "lucide-react";
import { useAuth, detailText } from "./auth";
import { BASE, PLATFORM, TOP_UP_URL } from "./platform";

const LS_KEY = "hedaply_credits_low";
const LS_NOTIFIED_AT = "hedaply_credits_notified_at";

const CreditAlertCtx = createContext({
  triggerCreditAlert: () => {},
  clearCreditAlert: () => {},
});

/**
 * Listens for 402 (credit-exhausted) signals from any AI endpoint and shows
 * a sticky owner-only banner + fires a browser/phone notification (works on
 * installed PWA on iOS 16.4+ and Android). Staff and customers see nothing.
 *
 * Usage:
 *   - Wrap the app with <CreditAlertProvider>
 *   - In any render/edit/variation/transcribe error handler, call
 *     useCreditAlert().triggerCreditAlert(res, data) with the response and
 *     its parsed body (no arguments for a failed job's "quota" kind).
 *
 * Under the Heda Platform the AI key is the platform's business and credits
 * come from the workspace wallet: the owner banner is off, and a 402 / 403
 * from the wallet (X-Platform-Credits) shows its message with a "Top up" link
 * to everyone instead.
 */
export function CreditAlertProvider({ children }) {
  const { user } = useAuth();
  const [low, setLow] = useState(() => !PLATFORM && localStorage.getItem(LS_KEY) === "1");
  const [wallet, setWallet] = useState("");
  const lastNotifAt = useRef(parseInt(localStorage.getItem(LS_NOTIFIED_AT) || "0", 10));

  // Ask owner for notification permission once, soft — fail silently if user denies
  useEffect(() => {
    if (PLATFORM || user?.role !== "owner") return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      // Slight delay so it doesn't fire on first paint
      const t = setTimeout(() => {
        try {
          Notification.requestPermission().catch(() => {});
        } catch {
          /* old browsers */
        }
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const fireNotification = useCallback(() => {
    try {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      // Throttle: at most once every 5 minutes per device, even if many 402s land
      const now = Date.now();
      if (now - lastNotifAt.current < 5 * 60 * 1000) return;
      lastNotifAt.current = now;
      localStorage.setItem(LS_NOTIFIED_AT, String(now));
      new Notification("Hedaply AI · Credits low", {
        body: "AI credits are exhausted. Please top up the Universal Key to resume renders.",
        icon: `${BASE}/heda-icon-192.png`,
        badge: `${BASE}/heda-icon-192.png`,
        tag: "hedaply-credits-low",
        requireInteraction: false,
      });
    } catch {
      /* notification failed — banner alone is enough */
    }
  }, []);

  const triggerCreditAlert = useCallback((res, data) => {
    if (PLATFORM) {
      if (res && res.headers && res.headers.get("X-Platform-Credits")) {
        setWallet(detailText(data, "Not enough AI credits."));
      }
      return;
    }
    if (res && res.status !== 402) return;
    // Only owners see / get notified — staff never know
    if (user?.role !== "owner") return;
    const wasLow = localStorage.getItem(LS_KEY) === "1";
    localStorage.setItem(LS_KEY, "1");
    setLow(true);
    if (!wasLow) fireNotification();
  }, [user, fireNotification]);

  const clearCreditAlert = useCallback(() => {
    localStorage.removeItem(LS_KEY);
    setLow(false);
    setWallet("");
  }, []);

  const showBanner = user?.role === "owner" && low;

  return (
    <CreditAlertCtx.Provider value={{ triggerCreditAlert, clearCreditAlert }}>
      {showBanner && <CreditAlertBanner onDismiss={clearCreditAlert} />}
      {wallet && <WalletBanner message={wallet} onDismiss={clearCreditAlert} />}
      {children}
    </CreditAlertCtx.Provider>
  );
}

export function useCreditAlert() {
  return useContext(CreditAlertCtx);
}

function WalletBanner({ message, onDismiss }) {
  return (
    <div
      data-testid="wallet-alert-banner"
      className="sticky top-0 z-50 flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 shadow-sm sm:px-5"
    >
      <AlertTriangle size={16} className="shrink-0 text-amber-600" />
      <div className="flex-1 text-[12.5px] leading-snug sm:text-[13px]">
        {message}{" "}
        <a href={TOP_UP_URL} className="font-semibold underline">
          Top up
        </a>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded-full p-1 text-amber-700 transition hover:bg-amber-100"
        title="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function CreditAlertBanner({ onDismiss }) {
  const granted = typeof Notification !== "undefined" && Notification.permission === "granted";
  return (
    <div
      data-testid="credit-alert-banner"
      className="sticky top-0 z-50 flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 shadow-sm sm:px-5"
    >
      <AlertTriangle size={16} className="shrink-0 text-amber-600" />
      <div className="flex-1 text-[12.5px] leading-snug sm:text-[13px]">
        <span className="font-semibold">AI credits low</span> · Please top up the Universal
        Key to resume renders.{" "}
        <span className="text-amber-700/80">(Only you see this — staff don't)</span>
      </div>
      {granted && (
        <span
          className="hidden items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 sm:inline-flex"
          title="Phone notifications enabled"
        >
          <BellRing size={10} /> Push on
        </span>
      )}
      <button
        type="button"
        data-testid="credit-alert-dismiss"
        onClick={onDismiss}
        className="rounded-full p-1 text-amber-700 transition hover:bg-amber-100"
        title="Dismiss (will return on next credit error)"
      >
        <X size={14} />
      </button>
    </div>
  );
}
